/**
 * One websocket per chat surface carrying every chat socket it opens, so
 * opening or switching chats costs a frame on a live connection instead of a
 * handshake.
 * Each subscription is a chat websocket path the server runs as its own
 * socket; `server_v2/v1/controllers/chats/mux.py` documents the framing.
 */

export interface ChatSocketSubscriber {
  onOpen(): void
  onClose(): void
  onMessage(data: string): void
}

export interface ChatSocketSubscription {
  send(text: string): void
  close(): void
}

const MUX_PATH = '/api/v1/chat/protocol/v3/mux'
const PING_EVERY_MS = 2_000
// A ping unanswered this long means the connection died without a close.
const DEAD_AFTER_MS = 6_000
const MAX_RETRY_MS = 10_000
// How long the connection outlives its last subscriber, for the next chat to reuse.
const LINGER_MS = 60_000

/** Full-jitter exponential backoff, from an immediate first retry up to MAX_RETRY_MS. */
const retryDelay = (attempt: number) =>
  attempt === 0 ? 0 : Math.random() * Math.min(MAX_RETRY_MS, 100 * 2 ** attempt)

interface Subscription {
  path: string
  subscriber: ChatSocketSubscriber
  opened: boolean
  outbox: string[]
  retries: number
  retryTimer?: ReturnType<typeof setTimeout>
}

class ChatSocketMux {
  private socket: WebSocket | null = null
  private connected = false
  private auth = ''
  private protocols: string[] = []
  private retries = 0
  private heardAt = 0
  private pingedAt: number | null = null
  private nextId = 0
  private readonly subscriptions = new Map<string, Subscription>()
  private livenessTimer?: ReturnType<typeof setInterval>
  private retryTimer?: ReturnType<typeof setTimeout>
  private lingerTimer?: ReturnType<typeof setTimeout>

  private readonly resume = () => {
    if (document.visibilityState !== 'visible' || !this.subscriptions.size)
      return
    if (!this.connected) this.connect()
    else this.ping()
  }

  constructor(private readonly url: string) {
    window.addEventListener('online', this.resume)
    document.addEventListener('visibilitychange', this.resume)
  }

  /** Closes the connection for good, dropping every subscription. */
  close(): void {
    window.removeEventListener('online', this.resume)
    document.removeEventListener('visibilitychange', this.resume)
    clearTimeout(this.retryTimer)
    clearTimeout(this.lingerTimer)
    this.drop()
    this.subscriptions.clear()
  }

  /** The subprotocols the next connection and every later open authenticate with. */
  setProtocols(protocols: string[]): void {
    this.protocols = protocols
  }

  /**
   * `auth` names the auth cookie generation: a new one reconnects, while a
   * refreshed access token only reaches subscriptions opened after it.
   */
  subscribe(
    path: string,
    auth: string,
    subscriber: ChatSocketSubscriber,
  ): ChatSocketSubscription {
    const id = String(this.nextId++)
    const subscription: Subscription = {
      path,
      subscriber,
      opened: false,
      outbox: [],
      retries: 0,
    }
    this.subscriptions.set(id, subscription)
    clearTimeout(this.lingerTimer)
    if (auth !== this.auth) {
      this.auth = auth
      this.connect()
    } else if (!this.socket) {
      this.connect()
    } else if (this.connected) {
      this.open(id, path)
    }
    return {
      send: (text) => {
        if (subscription.opened) this.socket?.send(`${id}\n${text}`)
        else subscription.outbox.push(text)
      },
      close: () => {
        if (!this.subscriptions.delete(id)) return
        clearTimeout(subscription.retryTimer)
        if (this.connected) this.control({ op: 'close', id })
        if (!this.subscriptions.size) {
          this.lingerTimer = setTimeout(() => this.drop(), LINGER_MS)
        }
      },
    }
  }

  private connect(): void {
    this.drop()
    clearTimeout(this.retryTimer)
    const socket = new WebSocket(this.url, this.protocols)
    socket.binaryType = 'arraybuffer'
    this.socket = socket
    socket.onopen = () => {
      this.connected = true
      this.retries = 0
      this.heardAt = Date.now()
      this.pingedAt = null
      for (const [id, { path }] of this.subscriptions) this.open(id, path)
    }
    socket.onmessage = (event: MessageEvent<string | ArrayBuffer>) => {
      this.heardAt = Date.now()
      this.pingedAt = null
      const frame =
        typeof event.data === 'string'
          ? event.data
          : new TextDecoder().decode(event.data)
      if (frame === 'pong') return
      if (frame.startsWith('{')) {
        this.onControl(JSON.parse(frame))
        return
      }
      const split = frame.indexOf('\n')
      this.subscriptions
        .get(frame.slice(0, split))
        ?.subscriber.onMessage(frame.slice(split + 1))
    }
    socket.onclose = () => {
      if (socket !== this.socket) return
      this.drop()
      if (this.subscriptions.size) {
        this.retryTimer = setTimeout(
          () => this.connect(),
          retryDelay(this.retries++),
        )
      }
    }
    this.livenessTimer = setInterval(
      () => this.checkLiveness(),
      PING_EVERY_MS / 4,
    )
  }

  /** Forgets the current socket, telling every opened subscription it closed. */
  private drop(): void {
    const socket = this.socket
    if (!socket) return
    this.socket = null
    this.connected = false
    clearInterval(this.livenessTimer)
    socket.onopen = socket.onmessage = socket.onclose = null
    socket.close()
    for (const subscription of this.subscriptions.values()) {
      clearTimeout(subscription.retryTimer)
      if (subscription.opened) {
        subscription.opened = false
        subscription.subscriber.onClose()
      }
    }
  }

  private onControl(control: { op: string; id: string; code?: number }): void {
    const subscription = this.subscriptions.get(control.id)
    if (!subscription) return
    if (control.op === 'opened') {
      subscription.opened = true
      subscription.retries = 0
      for (const text of subscription.outbox.splice(0)) {
        this.socket?.send(`${control.id}\n${text}`)
      }
      subscription.subscriber.onOpen()
    } else if (control.op === 'closed') {
      subscription.opened = false
      subscription.subscriber.onClose()
      subscription.retryTimer = setTimeout(
        () => {
          if (
            this.subscriptions.get(control.id) === subscription &&
            this.connected
          ) {
            this.open(control.id, subscription.path)
          }
        },
        retryDelay(++subscription.retries),
      )
    }
  }

  private checkLiveness(): void {
    if (!this.connected) return
    if (this.pingedAt === null) {
      if (Date.now() - this.heardAt >= PING_EVERY_MS) this.ping()
    } else if (Date.now() - this.pingedAt >= DEAD_AFTER_MS) {
      console.warn(
        `chat socket silent ${DEAD_AFTER_MS}ms after ping, replacing it`,
      )
      this.connect()
    }
  }

  private ping(): void {
    if (this.pingedAt !== null) return
    this.socket?.send('ping')
    this.pingedAt = Date.now()
  }

  private open(id: string, path: string): void {
    this.control({ op: 'open', id, path, protocols: this.protocols })
  }

  private control(control: object): void {
    this.socket?.send(JSON.stringify(control))
  }
}

/** A chat surface's muxes, one per API host. */
export class ChatSocketMuxes {
  private readonly muxes = new Map<string, ChatSocketMux>()

  /** The mux to the API that `socketUrl` points at. */
  for(socketUrl: URL): ChatSocketMux {
    const url = `${socketUrl.protocol}//${socketUrl.host}${MUX_PATH}`
    let mux = this.muxes.get(url)
    if (!mux) {
      mux = new ChatSocketMux(url)
      this.muxes.set(url, mux)
    }
    return mux
  }

  clear(): void {
    for (const mux of this.muxes.values()) mux.close()
    this.muxes.clear()
  }
}
